import React, { useContext } from "react";

import { ShiftCrewSettings } from "@/app/application/admin-menu/settings/ShiftCrewSettings";
import { ShiftGeneralSettings } from "@/app/application/admin-menu/settings/ShiftGeneralSettings";
import { ShiftNameSettings } from "@/app/application/admin-menu/settings/ShiftNameSettings";
import { ShiftSystemSettings } from "@/app/application/admin-menu/settings/ShiftSystemSettings";
import { Spinner } from "@/shared/ui/Spinner";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import {
	Box,
	Button,
	Grid,
	Stack,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";

import {
	useFetchShiftCrews,
	useFetchShiftNames,
	useFetchShiftSystems,
} from "@/service/ShiftHomeQueries";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";



const ShiftSettingsPage = () => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const {
		data: shiftSystems = [],
		isLoading: isLoadingShiftSystems,
		isError: isErrorShiftSystems,
		refetch: refetchShiftSystems,
	} = useFetchShiftSystems();

	const {
		data: shiftNames = [],
		isLoading: isLoadingShiftNames,
		isError: isErrorShiftNames,
		refetch: refetchShiftNames,
	} = useFetchShiftNames();

	const {
		data: shiftCrews,
		isLoading: isLoadingShiftCrews,
		isError: isErrorShiftCrews,
		refetch: refetchShiftCrews,
	} = useFetchShiftCrews();

	const memoizedShiftSystems = Array.isArray(shiftSystems) ? shiftSystems : [];
	const memoizedShiftNames = Array.isArray(shiftNames) ? shiftNames : [];
	const memoizedShiftCrews = Array.isArray(shiftCrews) ? shiftCrews : [];

	if (isLoadingShiftSystems || isLoadingShiftNames || isLoadingShiftCrews) {
		return <Spinner text={GetLanguageValueAsString(translation, "Auto.App.Application.AdminMenu.Settings.ShiftSettingsPage.L55C24.LoadingShiftSettings", "Loading shift settings...")} />;
	}

	if (isErrorShiftSystems || isErrorShiftNames || isErrorShiftCrews) {
		return (
			<Box
				sx={{
					mt: 4,
					p: 4,
					textAlign: "center",
					borderRadius: 2,
					border: (theme) => `1px solid ${theme.palette.divider}`,
					bgcolor: (theme) =>
						theme.palette.mode === "dark"
							? "rgba(255,255,255,0.03)"
							: "rgba(0,0,0,0.02)",
				}}
			>
				<Typography variant="h6" color="error">
					<Translate String="Auto.App.Application.AdminMenu.Settings.ShiftSettingsPage.L71C44.FailedToLoadShiftSettingsPlease">Failed to load shift settings. Please try again.</Translate>
				</Typography>
				<Button
					variant="outlined"
					color="primary"
					onClick={() => {
						void refetchShiftSystems();
						void refetchShiftNames();
						void refetchShiftCrews();
					}}
					sx={{ mt: 2 }}
				>
					<Translate String="Auto.App.Application.AdminMenu.Settings.ShiftSettingsPage.L83C6.Retry">Retry</Translate>
				</Button>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				mt: 1,
				width: "100%",
				display: "flex",
				flexDirection: "column",
				gap: 2,
			}}
		>
			<Box
				sx={{
					borderRadius: 2,
					border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
					bgcolor: "background.paper",
					px: { xs: 2, sm: 3 },
					py: { xs: 1.5, sm: 1.75 },
				}}
			>
				<Stack direction="row" spacing={1.25} alignItems="center">
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: 36,
							height: 36,
							borderRadius: 1.5,
							bgcolor: alpha(theme.palette.primary.main, 0.1),
							color: "primary.main",
							flexShrink: 0,
						}}
					>
						<AccessTimeOutlinedIcon fontSize="small" />
					</Box>
					<Typography
						variant="h6"
						sx={{ fontWeight: 700, letterSpacing: "-0.025em" }}
					>
						<Translate String="Auto.App.Application.AdminMenu.Settings.ShiftSettingsPage.L128C7.ShiftSettings">Shift Settings</Translate>
					</Typography>
				</Stack>
			</Box>

			<ShiftGeneralSettings shiftSystems={memoizedShiftSystems} />

			<Box>
				<Grid container spacing={2.5} alignItems="stretch">
					<Grid item xs={12} md={6}>
						<ShiftSystemSettings data={memoizedShiftSystems} />
					</Grid>
					<Grid item xs={12} md={6}>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
							<ShiftNameSettings data={memoizedShiftNames} />
							<ShiftCrewSettings data={memoizedShiftCrews} />
						</Box>
					</Grid>
				</Grid>
			</Box>
		</Box>
	);
};

export default ShiftSettingsPage;
